import gql from 'graphql-tag';
import {
  GetEventCommentsQuery,
  Roles_Enum,
  useMarkCommentAsQuestionMutation,
  useDeleteCommentMutation,
} from 'lib/graphql-generated';
import { useHasClickedOutside } from 'lib/hooks/use-has-clicked-outside';
import { useUser } from 'lib/hooks/use-user';
import { useRef, useState } from 'react';

gql`
  mutation DeleteComment($id: bigint!) {
    delete_event_comment_by_pk(id: $id) {
      id
    }
  }
`;

gql`
  mutation MarkCommentAsQuestion($id: bigint!, $isQuestion: Boolean) {
    update_event_comment_by_pk(
      pk_columns: { id: $id }
      _set: { is_question: $isQuestion }
    ) {
      id
      comment
      is_question
      public_account {
        id
        display_name
      }
    }
  }
`;

interface Props {
  commentObject: GetEventCommentsQuery['event_comment'][0];
}

export const ChatComment = ({ commentObject }: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { userData } = useUser();
  const menuRef = useRef(null);
  const [mark] = useMarkCommentAsQuestionMutation({
    variables: { id: commentObject.id, isQuestion: !commentObject.is_question },
  });
  const [deleteQuestion] = useDeleteCommentMutation({
    variables: { id: commentObject.id },
  });

  useHasClickedOutside(menuRef, () => setShowModal(false));

  const handleMarkAsQuestion = () => {};
  const handleDelete = () => {};

  return (
    <>
      <div className='even:bg-gray-100 px-1 py-2 relative'>
        <div className='flex flex-col'>
          <span className='text-2xs'>
            {commentObject.public_account.display_name}
          </span>
          <span className=''>
            {commentObject.is_question && (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4 inline-block my-auto mr-2 text-yellow-dark'
                viewBox='0 0 20 20'
                fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z'
                  clipRule='evenodd'
                />
              </svg>
            )}
            {commentObject.comment}
          </span>
        </div>
        {userData?.role === Roles_Enum.Bookseller ? (
          <span className='absolute top-1 right-2' ref={menuRef}>
            <button
              type='button'
              onClick={() => setShowModal(!showModal)}
              tabIndex={-1}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5 text-gray-500'
                viewBox='0 0 20 20'
                fill='currentColor'>
                <path d='M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z' />
              </svg>
            </button>
            {showModal && (
              <div className='absolute origin-bottom-left border rounded-lg border-gray-400 p-2 z-10 bg-white shadow-lg'>
                <button
                  tabIndex={-1}
                  onClick={() => mark()}
                  type='button'
                  className={`${
                    commentObject.is_question
                      ? 'text-yellow-dark hover:text-red-dark'
                      : 'text-gray-500 hover:text-yellow-dark'
                  } px-2 py-1`}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z'
                      clipRule='evenodd'
                    />
                  </svg>
                </button>
                <button
                  tabIndex={-1}
                  onClick={() => deleteQuestion()}
                  type='button'
                  className='text-gray-500 hover:text-red-dark px-2 py-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </button>
              </div>
            )}
          </span>
        ) : (
          commentObject.public_account.id === userData?.accountId && (
            <button
              tabIndex={-1}
              className='absolute top-2 right-2 hover:text-red-600 text-gray-500'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-3 w-3 '
                viewBox='0 0 20 20'
                fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          )
        )}
      </div>
    </>
  );
};
